/**
 * MapaMonitoreo.jsx
 * 
 * Componente de mapa para la página de monitoreo
 * Muestra la posición del repartidor en tiempo real
 * 
 * Qué hace:
 * - Muestra la ubicación del repartidor
 * - Sigue automáticamente al repartidor cuando se mueve
 * - Muestra un marcador personalizado para el repartidor
 * 
 * Qué recibe (props):
 * - posicionRepartidor (Object): {lat, lng} de la posición actual del repartidor
 * - activo (boolean): Si el repartidor está activo
 * 
 * Qué devuelve:
 * - JSX con el componente del mapa
 */

import React, { useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'

// Importar iconos de Leaflet
import 'leaflet/dist/leaflet.css'

// Fix para los iconos de Leaflet en React
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

/**
 * Componente que hace que el mapa siga al repartidor
 */
function SeguirRepartidor({ posicionRepartidor, activo }) {
  const map = useMap()

  useEffect(() => {
    if (!activo || !posicionRepartidor) return

    // Seguir al repartidor con panTo (movimiento suave)
    map.panTo(
      [posicionRepartidor.lat, posicionRepartidor.lng],
      { animate: true, duration: 0.6, easeLinearity: 0.25 }
    )

    // Ajustar zoom para una buena vista
    if (map.getZoom() < 16) {
      map.setZoom(16, { animate: true, duration: 0.5 })
    }
  }, [posicionRepartidor, activo, map])

  return null
}

/**
 * Componente principal del mapa de monitoreo
 */
function MapaMonitoreo({ 
  posicionRepartidor = null,
  activo = false
}) {
  // Coordenadas por defecto (Buenos Aires, Argentina)
  const centroPorDefecto = [-34.6037, -58.3816]
  const zoomPorDefecto = 13

  // Crear icono personalizado para el repartidor
  const iconoRepartidor = L.divIcon({
    className: 'marcador-repartidor-monitoreo',
    html: '<div style="background-color: #ef4444; width: 35px; height: 35px; border-radius: 50%; border: 4px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; color: white; font-size: 20px;">🚚</div>',
    iconSize: [35, 35],
    iconAnchor: [17.5, 17.5]
  })

  // Calcular centro del mapa
  let centro = centroPorDefecto
  if (posicionRepartidor) {
    centro = [posicionRepartidor.lat, posicionRepartidor.lng]
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <MapContainer
        center={centro}
        zoom={posicionRepartidor ? 16 : zoomPorDefecto}
        style={{ height: '100%', width: '100%' }}
      >
        {/* Capa de tiles (mapa base) */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Seguir al repartidor si está activo */}
        {activo && posicionRepartidor && (
          <SeguirRepartidor 
            posicionRepartidor={posicionRepartidor}
            activo={activo}
          />
        )}

        {/* Marcador del repartidor */}
        {posicionRepartidor && (
          <Marker position={[posicionRepartidor.lat, posicionRepartidor.lng]} icon={iconoRepartidor}>
            <Popup>
              <strong>Repartidor</strong>
              <p>Posición actual</p>
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                Lat: {posicionRepartidor.lat.toFixed(6)}<br />
                Lng: {posicionRepartidor.lng.toFixed(6)}
              </p>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Estilos CSS para la animación del pulso */}
      <style>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
          }
        }
        .marcador-repartidor-monitoreo > div {
          animation: pulse 2s infinite;
        }
      `}</style>
    </div>
  )
}

export default MapaMonitoreo

